// Supix is our own pixel service, here are some utils to register
// events, revoke permissions and parse information that should be
// sent to italic
import { getUtmsFromSession } from '../sessionStorage';
import { parseToBool } from '../string';
import { isBrowser, isDev, log as logger } from '../utils';

import APP from 'constants/app';
import {
  SEARCH_RESULTS_SUPIX_CONTEXT,
  SEARCH_RESULTS_SUPIX_VERTICAL,
} from 'constants/searchResults';

const isSupixEnabled = parseToBool(process.env.NEXT_PUBLIC_SUPIX_ENABLED);
const isSupixDebugEnabled = parseToBool(
  process.env.NEXT_PUBLIC_SUPIX_DEBUG_ENABLED
);

const log = (...args) => logger('supix', ...args);

export const getSearchContext = (headers) => {
  const { referer } = headers;
  let refererValue = -1;

  if (referer === `${APP.URL}/`) {
    return 0;
  }

  if (referer) {
    SEARCH_RESULTS_SUPIX_CONTEXT.forEach((v, i) => {
      if (referer.includes(v)) {
        refererValue = i;
      }
    });
  }

  return refererValue;
};

export const getSearchVertical = (type) =>
  SEARCH_RESULTS_SUPIX_VERTICAL[type] || -1;

/**
 * Reserved test/inert event ID from the generic-category registry: context
 * `0000` is documented as "Test / AVAILABLE — do not use for anything real",
 * so nothing joins against it and no dashboard counts it.
 *
 * See `kb/guides/supix-generic-category-events.md`.
 */
const CANARY_CATEGORY = 100000001;

/**
 * TEMPORARY — infra canary for the EKS migration. Remove once the cutover is
 * done; it is referenced in the migration completion notes.
 *
 * Fires one inert `/car` event and REPORTS the outcome to the console, instead
 * of swallowing it like `sendSupixEvent` does. The point is to prove the whole
 * browser-side path works from a given origin: CORS, the `sh` cookie, and the
 * Referer allowlist on `supix.promobit.com.br` — which rejects any host not on
 * it, silently, with a 200.
 *
 * DELIBERATELY bypasses `NEXT_PUBLIC_SUPIX_ENABLED`. That flag is `false` on
 * release, so routing this through `sendSupixEvent` would log and send
 * nothing — it would "pass" on release while validating none of the network
 * path, which is the exact blind spot this exists to close.
 *
 * Safe to run in production: the category is the reserved inert ID, and no
 * `user_id` or UTMs are attached, so nothing is attributable or joinable.
 *
 * Reachable only via `?supix_canary=1`. Organic traffic never sets it, so
 * volume is exactly what we fire.
 */
export const sendSupixCanary = async () => {
  if (!isBrowser()) {
    return;
  }

  const url = `${process.env.NEXT_PUBLIC_SUPIX_URL}/car/?category=${CANARY_CATEGORY}`;
  const context = { category: CANARY_CATEGORY, origin: window.location.origin };

  try {
    const response = await fetch(url, { mode: 'cors', credentials: 'include' });

    /**
     * Reading `status` at all requires the CORS check to have passed. If the
     * Referer is not allowlisted there is no ACAO header, fetch rejects, and we
     * land in the catch below — which is the failure we are looking for.
     */
    log('canary', {
      ...context,
      ok: response.ok,
      status: response.status,
      type: response.type,
    });
  } catch (error) {
    log('canary', { ...context, ok: false, error: String(error) });
  }
};

export const sendSupixEvent = async (key, data) => {
  if (!isBrowser()) {
    return;
  }

  const options = { ...data, ...getUtmsFromSession() };

  if (isSupixDebugEnabled) {
    log(`Event sent to endpoint "${key}" with data: `, options);
  }

  if (!isSupixEnabled) {
    return;
  }

  const optionsQs = Object.keys(options)
    .map((optionKey) => `${optionKey}=${options[optionKey]}`)
    .join('&');

  try {
    await fetch(`${process.env.NEXT_PUBLIC_SUPIX_URL}${key}/?${optionsQs}`, {
      mode: isDev() ? 'no-cors' : 'cors',
      credentials: 'include',
    });
  } catch (err) {
    const { captureException } = await import('@sentry/nextjs');

    captureException(err, {
      contexts: {
        data: {
          key,
          optionsQs,
          ...data,
        },
      },
    });
  }
};
